'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';

const FEATURES = [
  { icon: '📄', label: 'Resume Builder' },
  { icon: '🔍', label: 'ATS Analysis' },
  { icon: '🎓', label: 'Scholarships' },
  { icon: '🤖', label: 'AI Career Tools' },
  { icon: '🎬', label: 'Video Templates' },
  { icon: '🎯', label: 'Career Match' },
];

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 pt-28 pb-20 px-4 sm:px-6">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-blue-800/40 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-3xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Headline */}
          <div
            className="flex flex-col gap-6"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-white text-xs font-semibold backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
              </span>
              Free to start · No credit card required
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              Build Your Future{' '}
              <span className="text-blue-200">With Confidence</span>
            </h1>

            <p className="text-blue-100 text-base sm:text-lg leading-relaxed max-w-lg">
              Create a standout resume, find real job opportunities, explore verified scholarships, and get AI-powered career support — all in one place.
            </p>

            <p className="text-blue-200/80 text-sm font-medium">Better Tools. Brighter Opportunities.</p>

            <div className="flex flex-wrap gap-3">
              <Link
                href="/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-blue-700 font-bold text-sm hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                Get Started Free →
              </Link>
              <Link
                href="/resume-builder"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/15 text-white font-semibold text-sm border border-white/25 hover:bg-white/25 transition-all backdrop-blur-sm"
              >
                Build My Resume
              </Link>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 pt-2">
              {FEATURES?.map(f => (
                <span
                  key={f?.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium backdrop-blur-sm"
                >
                  <span>{f?.icon}</span>
                  {f?.label}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Product card */}
          <div
            className="relative"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
            }}
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-blue-100">
              {/* Card header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 17l4-8 4 4 4-6 4 10" />
                    </svg>
                  </div>
                  <span className="text-white font-bold text-sm">CareerLift</span>
                </div>
                <span className="text-xs text-blue-200 font-medium">Resume Builder</span>
              </div>

              {/* Resume preview */}
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-blue-50">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">JD</div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">Your Name Here</div>
                    <div className="text-xs text-slate-500">Software Engineer · San Francisco, CA</div>
                  </div>
                  <div className="ml-auto">
                    <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 font-semibold border border-green-200">ATS Ready</span>
                  </div>
                </div>

                {/* ATS score */}
                <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-3">
                  <div className="relative w-12 h-12 shrink-0">
                    <svg width="48" height="48" viewBox="0 0 48 48" className="absolute inset-0">
                      <circle cx="24" cy="24" r="20" fill="none" stroke="#dbeafe" strokeWidth="4" />
                      <circle cx="24" cy="24" r="20" fill="none" stroke="#3b82f6" strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 20}`}
                        strokeDashoffset={`${2 * Math.PI * 20 * 0.22}`}
                        transform="rotate(-90 24 24)"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-blue-700">78%</span>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-700">ATS Score Estimate</div>
                    <div className="text-xs text-slate-500">3 improvements suggested</div>
                  </div>
                </div>

                {/* Suggestions */}
                <div className="space-y-2">
                  {[
                    { icon: '✅', text: 'Strong action verbs detected', color: 'text-green-600' },
                    { icon: '💡', text: 'Add missing keywords from JD', color: 'text-blue-600' },
                    { icon: '💡', text: 'Improve bullet point specificity', color: 'text-blue-600' },
                  ]?.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span>{item?.icon}</span>
                      <span className={item?.color}>{item?.text}</span>
                    </div>
                  ))}
                </div>

                <Link
                  href="/resume-builder"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors"
                >
                  Build My Resume →
                </Link>
              </div>
            </div>

            {/* Floating badges */}
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg border border-blue-100 px-3 py-2 flex items-center gap-2">
              <span className="text-lg">🎓</span>
              <div>
                <div className="text-xs font-bold text-slate-800">Scholarships</div>
                <div className="text-xs text-slate-500">Verified sources</div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg border border-blue-100 px-3 py-2 flex items-center gap-2">
              <span className="text-lg">🤖</span>
              <div>
                <div className="text-xs font-bold text-slate-800">AI Career Tools</div>
                <div className="text-xs text-slate-500">No fabrication</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
